import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import * as SalesforceActions from './salesforce.actions';
import { WorkflowManagementControllerService } from '@rappider/rappider-sdk';
import { DataSourceControllerService } from '@rappider/rappider-sdk';
import { Router } from '@angular/router';
import { NotificationService } from '@rappider/services';

@Injectable()
export class SalesforceEffects {
  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private workflowManagementControllerService: WorkflowManagementControllerService,
    private router: Router,
    private dataSourceControllerService: DataSourceControllerService,
    private notificationService: NotificationService
  ) { }

  salesforceLoginAndGetSelectedObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SalesforceActions.SalesforceLoginAndGetSelectedObjects
      ),
      mergeMap((action) => {
        const params = {
          body: {
            name: 'salesforceLoginAndGetTopUsedStandardSObjectsAndCustomSObjects',
            payload: {
              username: action.payload.username,
              password: action.payload.password,
              token: action.payload.token
            }
          }
        };
        return this.workflowManagementControllerService.runWorkflowStepFunctionSyncWithJWT(params).pipe(
          mergeMap((objects) => {
            return [
              SalesforceActions.SalesforceLoginAndGetSelectedObjectsSuccessful({ payload: { selectedObjects: objects?.sObjectNames, username: action.payload.username, password: action.payload.password, token: action.payload.token } }),
            ];
          }), catchError(error => {
            return [
              SalesforceActions.SalesforceLoginAndGetSelectedObjectsFailure({ payload: { error, key: 'Salesforce', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  salesforceLoginAndGetObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SalesforceActions.SalesforceLoginAndGetSelectedObjectsSuccessful
      ),
      mergeMap((action) => {
        const params = {
          body: {
            name: 'salesforceLoginAndGetSObjects',
            payload: {
              username: action.payload.username,
              password: action.payload.password,
              token: action.payload.token
            }
          }
        };
        return this.workflowManagementControllerService.runWorkflowStepFunctionSyncWithJWT(params).pipe(
          map((objects) => {
            this.router.navigate(['datasources/salesforce-model-list']);
            return SalesforceActions.SalesforceLoginAndGetObjectsSuccessful({ payload: { objects: objects?.sObjectNames } });
          }), catchError(error => {
            return [
              SalesforceActions.SalesforceLoginAndGetObjectsFailure({ payload: { error, key: 'Salesforce', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  findDataSourceName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SalesforceActions.GetSalesforceObjectName
      ),
      mergeMap((action) => {
        const params = {
          filter: {
            fields: {
              name: true
            }
          }
        };
        return this.dataSourceControllerService.find(params).pipe(
          map((datasources) => {
            return SalesforceActions.CreateSalesforceObject({ payload: { frequency: action.payload.frequency, objects: action.payload.objects, currentObjectNames: datasources } });
          }), catchError(error => {
            return [
              SalesforceActions.CreateSalesforceObjectFailure({ payload: { error, key: 'GetSalesforceObjectName', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  createSalesforceObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SalesforceActions.CreateSalesforceObject
      ),
      withLatestFrom(
        this.store.select(state => state.salesforce.user),
      ),
      mergeMap(([action, user]) => {
        function getUniqueSalesforceName(existingNames: string[]): string {
          let index = 1;
          let newName = 'Salesforce';
          while (existingNames.includes(newName)) {
            index++;
            newName = `Salesforce${index}`;
          }

          return newName;
        }
        const params = {
          body: {
            name: getUniqueSalesforceName(action.payload.currentObjectNames?.map(object => object.name)),
            connector: 'salesforce',
            config: {
              username: user.username,
              password: user.password,
              token: user.token,
              frequency: action.payload.frequency,
              model: action.payload.objects
            }
          }
        };
        return this.dataSourceControllerService.create(params).pipe(
          map((datasource) => {
            this.router.navigate(['ai/datasources']);
            return SalesforceActions.CreateSalesforceObjectSuccessful();
          }), catchError(error => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              error?.error?.error?.message || 'Create Datasource Failed'
            );
            return [
              SalesforceActions.CreateSalesforceObjectFailure({ payload: { error, key: 'CreateSalesforceObject', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );
}
